import {NewMarkdownViewer} from '@github-ui/markdown-viewer/NewMarkdownViewer'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {Label, Link, RelativeTime} from '@primer/react'
import {ExpandableContent} from './ExpandableContent'
import styles from './Description.module.css'

type OverviewDescriptionProps = {
  descriptionHtml: SafeHTMLString | null
  createdTime?: string
  author: {
    login: string
    avatarUrl?: string | null
  }
}

/** PR description section on the Overview tab with expandable overflow */
export function Description({descriptionHtml, author, createdTime}: OverviewDescriptionProps) {
  const isEmptyDescription = !descriptionHtml || descriptionHtml === ''
  // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- static string literal is safe
  const placeholderText = 'No description provided.' as SafeHTMLString

  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const isAI = isCopilot(author.login)
  const profileUrl = `/${isAI ? 'apps/' : ''}${author.login}`
  const hovercardAttributes = isAI
    ? getHovercardAttributesForType('copilot', {botSlug: author.login})
    : getHovercardAttributesForType('user', {login: author.login})

  return (
    <section className={styles.container} aria-label="Description">
      <div className={styles.header}>
        {author.avatarUrl ? (
          <Link href={profileUrl} {...hovercardAttributes} aria-label={`${author.login}'s profile`}>
            <GitHubAvatar src={author.avatarUrl} size={24} />
          </Link>
        ) : null}
        <span className="d-flex flex-row gap-1 flex-items-center">
          <Link href={profileUrl} {...hovercardAttributes} className="fgColor-default">
            <strong>{author.login}</strong>
          </Link>
          {isAI && (
            <Label variant="secondary" className="ml-1">
              AI
            </Label>
          )}
          <span className="fgColor-muted d-flex flex-row gap-1 flex-items-center">
            opened
            {createdTime && <RelativeTime date={new Date(createdTime)} />}
          </span>
        </span>
      </div>
      <ExpandableContent sectionLabel="Description" storageKey="overview-description-expanded" defaultExpanded>
        <NewMarkdownViewer
          verifiedHTML={isEmptyDescription ? placeholderText : descriptionHtml}
          className={isEmptyDescription ? 'fgColor-muted text-italic' : undefined}
        />
      </ExpandableContent>
    </section>
  )
}
